import { useContext } from 'react';
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { ChatContext } from "../context/ChatContext";
import { AssistantContext } from "../context/AssistantContext";
import { ConversationsContext } from '../context/ConversationsContext';
import { useNavigate } from 'react-router-dom';
import { createNewConversation } from "../api/chatApi";
import { authFetch, getAccessToken, refreshToken, isTokenExpired, redirectToLogin } from "../utils/auth";

const useChatSSE = () => {
  const { settings } = useContext(AssistantContext);
  const { messages, addToStreamMessage, addToMessages, setMessages, setError, setStreamFailed, setStatusMessage } = useContext(ChatContext);
  let messageId = null;
  const { refreshConversations } = useContext(ConversationsContext);
  const navigate = useNavigate();

  const sendMessage = async (userMessage, fileAsBase64, conversationId) => {
    const url = "/api/";
    let newConversationId = conversationId;
    setError(null); // Clear any existing errors
    setStreamFailed(false);

    // If there is no conversationId, create a new conversation
    if (!conversationId) {
      try {
        const newConversation = await createNewConversation();
        newConversationId = newConversation.id;
        navigate(`/c/${newConversationId}`);
        setMessages([]);
      } catch (error) {
        const errorMessage = error.message || "Failed to create a new conversation";
        setError(errorMessage);
        setStreamFailed(true);
        return;
      }
    }

    addToMessages({ role: "user", content: userMessage });
    addToStreamMessage(null);
    setStatusMessage(null, null); // Clear any previous status messages

    // Only send the last user message instead of all messages
    const newMessages = [{ role: "user", content: userMessage }];
    let localStreamMessage = "";

    try {
      // Only refresh the token if it's expired or about to expire
      if (isTokenExpired()) {
        const refreshed = await refreshToken();
        if (!refreshed) {
          // If refresh failed, redirect to login
          redirectToLogin();
          return;
        }
      }
      
      // Build payload for V2 API
      const mode = settings.mode || "chat";
      let config = {};
      if (mode === "chat") {
        config = { model: settings.chat.selectedLLM };
      } else if (mode === "agent") {
        config = { tools: settings.agent.selectedTools };
      } else if (mode === "assistant") {
        const solutions = (settings.assistant.selectedSolutions || []).map(sol =>
          sol.toLowerCase().replace(/\s+/g, "-")
        );
        config = { solutions };
      }
      const files = fileAsBase64 ? [{ data: fileAsBase64 }] : undefined;
      const payload = {
        conversationID: newConversationId,
        message: newMessages,
        mode,
        config,
        ...(files ? { files } : {})
      };

      await fetchEventSource(url + "chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getAccessToken()}`
        },
        body: JSON.stringify(payload),
        openWhenHidden: true,
        // Only use keepalive if there's no file being uploaded or the first file is under 50KB
        keepalive: !fileAsBase64 || fileAsBase64.length < 60 * 1024,
        credentials: 'omit',
        retry: {
          retries: 3,
          initialDelayMs: 1000,
          maxDelayMs: 5000
        },
        onopen(response) {
          if (!response.ok) {
            const errorMsg = `Failed to connect to the server: ${response.status} ${response.statusText}`;
            setError(errorMsg);
            setStreamFailed(true);
            // By returning a value (instead of throwing) we abort the connection without triggering an uncaught exception.
            return;
          }
        },
        onmessage(event) {
          if (event.data) {
            try {
              const data = JSON.parse(event.data);
              if (data.error) {
                setError(data.error.message);
                setStreamFailed(true);
                return;
              }
              
              // Handle status messages - new format
              if (data.status) {
                // Extract the message and status_type from the nested status object
                const { message, status_type } = data.status;
                setStatusMessage(message, status_type);
                return;
              }
              
              // Handle regular message content
              if (data.message && data.message.content) {
                addToStreamMessage(data.message.content);
                localStreamMessage += data.message.content;
                if (data.id && !messageId) {
                  messageId = data.id;
                }
              }
            } catch (error) {
              console.warn("Failed to parse SSE message:", error);
            }
          }
        },
        async onerror(error) {
          // If we get a 401 error, try to refresh the token and let the retry logic handle reconnection
          if (error && error.status === 401) {
            const refreshed = await refreshToken();
            if (!refreshed) {
              // If refresh failed, redirect to login
              redirectToLogin();
              return;
            }
            // Let the retry logic handle reconnection with the new token
            return;
          }
          
          const errorMessage = error?.message || "Connection error occurred";
          setError(errorMessage);
          setStreamFailed(true);
          // Do not rethrow; returning prevents unhandled promise rejection which would surface as React runtime error overlay.
          return;
        },
        onclose() {
          try {
            if (messages.length <= 1) {
              updateConversationTitle(newConversationId);
            }
            if (localStreamMessage) {
              addToMessages({id: messageId, role: "assistant", content: localStreamMessage });
            }
            addToStreamMessage(null);
            setStatusMessage(null, null); // Clear status when stream is complete
            localStreamMessage = "";
          } catch (error) {
            setError("Failed to process final message");
          }
        },
      });
    } catch (error) {
      const errorMessage = error?.message || "Failed to send message";
      setError(errorMessage);
      setStreamFailed(true);
      addToStreamMessage(null);
    }
  };

  const updateConversationTitle = async (conversationId) => {
    try {
      const response = await authFetch(`/api/c/title`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ conversationID: conversationId }),
      });
      if (!response.ok) {
        throw new Error(`Failed to update conversation title: ${response.statusText}`);
      }
      refreshConversations();
    } catch (error) {
      const errorMessage = error?.message || "Failed to update conversation title";
      setError(errorMessage);
      setStreamFailed(true);
    }
  };

  return { sendMessage };
};

export default useChatSSE;
